import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import UseTemplateBtnIcon from 'components/atoms/icons/useTemplateBtnIcon';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import { useEffect, type FC } from 'react';
import DraggableModal from 'components/organisms/draggableModal';
import { useUnsavedChanges } from 'infrastructure/providers/unsaved-changes/unsaved-changes';

import { clinicalNotesValidationSchema } from './validation-schema';
import s from './styles.module.scss';

import type { ModuleType } from 'infrastructure/enums';
import type { FormikHelpers } from 'formik';
import type { Values } from './validation-schema';
import type { IAddClinicNotes } from 'infrastructure/interfaces';

interface ClinicalNotesFormProps {
  title: string;
  open: boolean;
  onCancelForm: () => void;
  onSubmit: (values: Values, formikHelpers: FormikHelpers<Values>) => void;
  loading: boolean;
  defaultValues?: Omit<IAddClinicNotes, 'patientGuid' | 'templateType'>;
  templateButtons: {
    onClick: () => void;
    module: ModuleType;
    label: string;
    dataCy: string;
    disabled?: boolean;
  }[];
}

const ClinicalNotesModalForm: FC<ClinicalNotesFormProps> = ({
  title,
  open,
  loading,
  onCancelForm,
  onSubmit,
  defaultValues,
  templateButtons,
}) => {
  const { t } = useTranslation();
  const { Alert, showAlert } = useAlert();

  const { addUnsavedItem, removeUnsavedItem, UnsavedForms } =
    useUnsavedChanges();

  const formik = useFormik<Values>({
    initialValues: clinicalNotesValidationSchema().cast(defaultValues),
    enableReinitialize: true,
    onSubmit,
    validateOnChange: false,
    validationSchema: clinicalNotesValidationSchema(),
  });

  const { submitForm, resetForm, values } = formik;

  const onCancel = async () => {
    if (!loading) {
      if (values.note) {
        const confirmRes = await showAlert({
          title: t('confirm.cancelNote'),
          messageTitle: t('confirm.closeClinicalNotePopup'),
          successBtnLabel: t('controls.yes'),
          rejectBtnLabel: t('controls.no'),
        });

        if (confirmRes) {
          resetForm();
          onCancelForm();
        }
      } else {
        resetForm();
        onCancelForm();
      }
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();
      removeUnsavedItem(UnsavedForms.clinicalNotes);
    }
  }, [open]);

  useEffect(() => {
    if (formik.dirty) {
      addUnsavedItem(UnsavedForms.clinicalNotes);
    } else {
      removeUnsavedItem(UnsavedForms.clinicalNotes);
    }
  }, [formik.dirty]);

  return (
    <>
      {open && (
        <DraggableModal
          title={title}
          isOpen={open}
          toggle={onCancel}
          bodyClassName={s['modal-body']}
          modalClassName={s.modal}
          footerClassName={s['modal-footer']}
        >
          <BaseForm
            formik={formik}
            withControls
            plaintext={false}
            readonly={false}
          >
            <BaseFormItem name="note">
              <BaseTextArea
                rows={10}
                placeholder="Type your note here"
                disabled={loading}
              />
            </BaseFormItem>
            <div className={s.footer}>
              <div className={s.left}>
                {templateButtons.map((btn) => {
                  return (
                    <BaseButton
                      className={s['use-template-button']}
                      label={btn.label}
                      key={btn.label}
                      dataCy={btn.dataCy}
                      type="secondary"
                      disabled={btn.disabled || loading}
                      icon={<UseTemplateBtnIcon />}
                      onClick={btn.onClick}
                    />
                  );
                })}
              </div>

              <div className={s.right}>
                <BaseButton
                  label={t('controls.cancel')}
                  dataCy="clinicalNotes-cancel-button"
                  type="secondary"
                  onClick={onCancel}
                  disabled={loading}
                />
                <BaseButton
                  label={t('controls.save')}
                  dataCy="clinicalNotes-save-button"
                  onClick={submitForm}
                  disabled={loading}
                  loading={loading}
                />
              </div>
            </div>
          </BaseForm>
        </DraggableModal>
      )}
      {Alert}
    </>
  );
};
export default ClinicalNotesModalForm;
